import { FC, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import EDateText from '@/components/everridge/EDateText';
import { Label } from '@/components/ui/label';
import {
    UserActivityListItemDto,
    UserActivitySource,
} from '@/features/superAdmin/features/userActivities/types/superAdmin-userActivities-dto';

type Props = {
    activity: UserActivityListItemDto;
    onClose: () => void;
    isMobile?: boolean;
};

type DetailFieldProps = {
    label: string;
    children: ReactNode;
    className?: string;
};

const DetailField: FC<DetailFieldProps> = ({ label, children, className }) => (
    <div className={cn('flex flex-col gap-1', className)}>
        <Label className="text-xs text-popover-600">{label}</Label>
        <div className="text-sm text-popover-900">{children}</div>
    </div>
);

const UserActivityDetailsDialog: FC<Props> = ({ activity, onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const getSourceLabel = (source: UserActivitySource) => {
        if (source === UserActivitySource.App) {
            return t('main.superAdmin.userActivities.source.app');
        }
        return t('main.superAdmin.userActivities.source.keycloak');
    };

    const tenantsText = useMemo(() => {
        const list = activity.assignedTenants ?? [];
        return list.map((x) => (x.name?.trim() ? x.name : x.tenantId)).join(', ');
    }, [activity.assignedTenants]);

    const parsedDetails = useMemo(() => {
        const details = activity.details?.trim();
        if (!details) return null;

        try {
            return JSON.parse(details) as Record<string, unknown>;
        } catch {
            return null;
        }
    }, [activity.details]);

    const renderDetails = () => {
        const details = activity.details?.trim();
        if (!details) return '—';

        if (parsedDetails) {
            const errorValue = parsedDetails.Error ?? parsedDetails.error;
            const hasError = errorValue !== undefined && errorValue !== null && errorValue !== '';

            return (
                <div className="flex flex-col gap-3">
                    {hasError && (
                        <div className="rounded-lg border border-error-200 bg-error-50 px-3 py-2">
                            <span className="text-xs font-medium text-error-800">
                                {t('main.superAdmin.userActivities.details.error')}
                            </span>
                            <p className="mt-1 text-sm text-error-900 break-words">
                                {String(errorValue)}
                            </p>
                        </div>
                    )}
                    <pre className="max-h-60 overflow-auto rounded-lg border border-popover-100 bg-popover-50 p-3 text-xs text-popover-900 whitespace-pre-wrap break-words">
                        {JSON.stringify(parsedDetails, null, 2)}
                    </pre>
                </div>
            );
        }

        return (
            <pre className="max-h-60 overflow-auto rounded-lg border border-popover-100 bg-popover-50 p-3 text-xs text-popover-900 whitespace-pre-wrap break-words">
                {details}
            </pre>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {t('main.superAdmin.userActivities.details.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <DetailField label={t('main.superAdmin.userActivities.table.header.date')}>
                        <EDateText date={activity.date} />
                    </DetailField>
                    <DetailField label={t('main.superAdmin.userActivities.table.header.source')}>
                        {getSourceLabel(activity.source)}
                    </DetailField>
                    <DetailField label={t('main.superAdmin.userActivities.table.header.eventType')}>
                        {activity.eventType}
                    </DetailField>
                    <DetailField label={t('main.superAdmin.userActivities.table.header.userName')}>
                        {activity.userName}
                    </DetailField>
                    <DetailField label={t('main.superAdmin.userActivities.table.header.tenants')}>
                        {tenantsText || '—'}
                    </DetailField>
                    <DetailField label={t('main.superAdmin.userActivities.table.header.details')}>
                        {renderDetails()}
                    </DetailField>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UserActivityDetailsDialog;
